import React, { useEffect } from "react";
import Chart from "chart.js";
import { SiteVariablesPrepared } from "@fluentui/react-northstar";
import { TeamsTheme } from "msTeamsUI/themes";
import { IChartData } from "../ChartTypes";
import {
    tooltipTrigger,
    chartConfig,
    axesConfig,
    setTooltipColorScheme,
    horizontalBarValue,
    usNumberFormat,
} from "../ChartUtils";
import { ChartContainer } from "./ChartContainer";
import { buildPattern, chartBarDataPointPatterns } from "../ChartPatterns";
import { getText } from "../../../translations/index";
import { IOutlabeledChartOptions } from "../IOutlabeledChartOptions";

export const BarHorizontalChart = ({
    title,
    data,
    siteVariables,
    tooltipHideFooter,
    presentsMoney,
    legendOnTheRightSide,
    hideLegend,
    chartContainerMinHeight,
    outlabeledChartOptions,
    stacked,
}: {
    title: string;
    data: IChartData;
    siteVariables: SiteVariablesPrepared;
    cutoutPercentage?: number;
    tooltipHideFooter?: boolean;
    presentsMoney?: boolean;
    legendOnTheRightSide?: boolean;
    hideLegend?: boolean;
    chartContainerMinHeight?: string;
    outlabeledChartOptions?: IOutlabeledChartOptions;
    stacked?: boolean;
}) => {
    const { colorScheme, theme, colors, t } = siteVariables;
    const canvasRef = React.useRef<HTMLCanvasElement | null>(null);
    const chartRef = React.useRef<Chart | undefined>();
    const chartId = React.useMemo(() => Math.random().toString(36).substr(2, 9), []);
    const chartDataPointColors = React.useMemo(
        () => [
            colors.brand["600"],
            colors.brand["200"],
            colors.brand["800"],
            colors.grey["400"],
            colors.grey["500"],
            colorScheme.default.borderHover,
        ],
        [theme]
    );

    const createDataPoints = (): Chart.ChartDataSets[] =>
        Array.from(data.datasets, (set, i) => {
            let dataPointConfig = {
                label: getText(t.locale, set.label),
                data: set.data,
                borderWidth: 0,
                barPercentage: 0.5,
                borderSkipped: false,
                borderColor: colorScheme.default.background,
                hoverBorderColor: chartDataPointColors[i],
                backgroundColor: chartDataPointColors[i],
                hoverBorderWidth: 0,
                hoverBackgroundColor: chartDataPointColors[i],
                pointBorderColor: colorScheme.default.background,
                pointBackgroundColor: colorScheme.default.foreground3,
                pointHoverBackgroundColor: colorScheme.default.foreground3,
                pointHoverBorderColor: chartDataPointColors[i],
                pointHoverBorderWidth: 0,
                borderCapStyle: "round",
                borderJoinStyle: "round",
                pointBorderWidth: 0,
                pointRadius: 0,
                pointHoverRadius: 0,
            };
            if (theme === TeamsTheme.HighContrast) {
                dataPointConfig = {
                    ...dataPointConfig,
                    borderWidth: 1,
                    hoverBorderColor: colorScheme.default.borderHover,
                    hoverBorderWidth: 3,
                    pointBorderColor: colorScheme.default.border,
                    pointHoverBorderColor: colorScheme.default.borderHover,
                    pointHoverRadius: 0,
                    borderColor: colorScheme.brand.background,
                    backgroundColor: buildPattern({
                        ...chartBarDataPointPatterns(colorScheme)[i],
                        backgroundColor: colorScheme.default.background,
                        patternColor: colorScheme.brand.background,
                    }),
                    hoverBackgroundColor: buildPattern({
                        ...chartBarDataPointPatterns(colorScheme)[i],
                        backgroundColor: colorScheme.default.background,
                        patternColor: colorScheme.default.borderHover,
                    }),
                };
            }
            return dataPointConfig as any;
        });

    useEffect(() => {
        let selectedIndex = -1;
        let selectedDataSet = 0;

        if (!canvasRef.current) return;
        const ctx = canvasRef.current.getContext("2d");
        if (!ctx) return;
        // const config: any = chartConfig({ type: "horizontalBar" });
        const config: any = chartConfig({
            type: "horizontalBar",
            presentsMoney: presentsMoney,
            tooltipHideFooter: tooltipHideFooter,
        });

        config.options.layout.padding.top = -6;
        config.options.layout.padding.left = -32;

        config.options.hover.mode = "index";

        config.options.scales.xAxes[0].ticks.display = false;
        config.options.scales.xAxes[0].gridLines.display = false;

        config.options.scales.yAxes[0].ticks.callback = (v: string) => v;
        config.options.scales.yAxes[0].ticks.mirror = true;
        config.options.scales.yAxes[0].ticks.padding = 0;
        config.options.scales.yAxes[0].gridLines.display = false;
        config.options.scales.yAxes[0].ticks.autoSkip = false;

        //config.options.tooltips.position = "nearest";

        if (stacked) {
            config.options.hover.mode = "point";

            config.options.scales.yAxes[0].stacked = true;
            config.options.scales.xAxes[0].stacked = true;
            config.options.tooltips.mode = "nearest";
            config.options.tooltips.axis = "y";
            config.options.tooltips.callbacks.title = (tooltipItems: any) => {
                let total = 0;
                data.datasets.map((dataset) => {
                    const value = dataset.data[tooltipItems[0].index];
                    if (typeof value === "number") {
                        return (total += value);
                    }
                });
                return `${((tooltipItems[0].xLabel / total) * 100).toPrecision(2)}% (${usNumberFormat(
                    tooltipItems[0].xLabel
                )})`;
            };
        }

        chartRef.current = new Chart(ctx, {
            ...(config as any),
            data: {
                labels: Array.isArray(data.labels)
                    ? data.labels.map((label) => getText(t.locale, label))
                    : getText(t.locale, data.labels),
                datasets: [],
            },
            plugins: [
                {
                    afterDatasetsDraw: ({ ctx, tooltip, chart }: any) => {
                        horizontalBarValue({
                            chart,
                            ctx,
                            stacked,
                            presentsMoney,
                        });
                    },
                },
            ],
        });

        const chart: any = chartRef.current;

        chart.config.options.scales.yAxes[0].ticks.labelOffset =
            chart.chartArea.bottom / data.datasets[0].data.length / 2 - 2;
        /**
         * Keyboard manipulations
         */
        function meta() {
            return chart.getDatasetMeta(selectedDataSet);
        }

        function removeFocusStyleOnClick() {
            // Remove focus state style if selected by mouse
            if (canvasRef.current) {
                canvasRef.current.style.boxShadow = "none";
            }
        }

        function removeDataPointsHoverStates() {
            const datasetMeta = meta();
            if (selectedIndex > -1 && datasetMeta.data[selectedIndex]) {
                datasetMeta.controller.removeHoverStyle(datasetMeta.data[selectedIndex], 0, selectedIndex);
            }
        }

        function hoverDataPoint(pointID: number) {
            const datasetMeta = meta();
            if (datasetMeta.data[pointID])
                datasetMeta.controller.setHoverStyle(datasetMeta.data[pointID], selectedDataSet, pointID);
        }

        function showFocusedDataPoint() {
            hoverDataPoint(selectedIndex);
            tooltipTrigger({
                chart: chartRef.current as any,
                data,
                set: selectedDataSet,
                index: selectedIndex,
                siteVariables,
            });
            document.getElementById(`${chartId}-tooltip-${selectedDataSet}-${selectedIndex}`)?.focus();
        }

        function resetChartStates() {
            removeDataPointsHoverStates();
            const activeElements = chart.tooltip._active;
            const requestedElem = chart.getDatasetMeta(selectedDataSet).data[selectedIndex];
            if (!activeElements) {
                return;
            }

            activeElements.find((v: any, i: number) => {
                if (requestedElem?._index === v?._index) {
                    activeElements.splice(i, 1);
                    return true;
                }
            });

            for (let i = 0; i < activeElements.length; i++) {
                if (requestedElem._index === activeElements[i]._index) {
                    activeElements.splice(i, 1);
                    break;
                }
            }
            if (siteVariables.theme === TeamsTheme.HighContrast) {
                (chartRef.current as any).data.datasets.map((dataset: any, i: number) => {
                    dataset.borderColor = siteVariables.colorScheme.default.border;
                    dataset.borderWidth = 2;
                    dataset.backgroundColor = buildPattern({
                        ...chartBarDataPointPatterns(colorScheme)[i],
                        backgroundColor: colorScheme.default.background,
                        patternColor: colorScheme.brand.background,
                    });
                });
                chart.update();
            }
            chart.tooltip._active = activeElements;
            chart.tooltip.update(true);
            chart.draw();
        }

        function changeFocus(e: KeyboardEvent) {
            removeDataPointsHoverStates();
            const datasetMeta = meta();
            switch (e.key) {
                case "ArrowDown":
                    e.preventDefault();
                    selectedIndex = (selectedIndex + 1) % datasetMeta.data.length;
                    break;
                case "ArrowUp":
                    e.preventDefault();
                    selectedIndex = (selectedIndex || datasetMeta.data.length) - 1;
                    break;
            }

            showFocusedDataPoint();
        }

        canvasRef.current.addEventListener("click", removeFocusStyleOnClick);
        canvasRef.current.addEventListener("keydown", changeFocus);
        canvasRef.current.addEventListener("focusout", resetChartStates);
        return () => {
            if (!chartRef.current) return;
            if (canvasRef.current) {
                canvasRef.current.removeEventListener("click", removeFocusStyleOnClick);
                canvasRef.current.removeEventListener("keydown", changeFocus);
                canvasRef.current.removeEventListener("focusout", resetChartStates);
            }
            chartRef.current.destroy();
        };
    }, []);

    /**
     * Theme updates
     */
    useEffect(() => {
        if (!chartRef.current) return;
        if (!canvasRef.current) return;
        const ctx = canvasRef.current.getContext("2d");
        if (!ctx) return;
        // Apply new colors scheme for data points
        chartRef.current.data.datasets = createDataPoints();
        // Update tooltip colors scheme
        setTooltipColorScheme({
            chart: chartRef.current,
            siteVariables,
            chartDataPointColors,
            patterns: chartBarDataPointPatterns,
        });
        // Update axeses
        axesConfig({ chart: chartRef.current, ctx, colorScheme });
        chartRef.current.options.defaultColor = colorScheme.default.foreground;
        chartRef.current.update();
    }, [theme]);

    function onLegendClick(datasetIndex: number) {
        if (!chartRef.current) return;
        chartRef.current.data.datasets![datasetIndex].hidden = !chartRef.current.data.datasets![datasetIndex].hidden;
        chartRef.current.update();
    }

    return (
        <ChartContainer
            siteVariables={siteVariables}
            data={data}
            chartDataPointColors={chartDataPointColors}
            patterns={chartBarDataPointPatterns}
            onLegendClick={onLegendClick}
            tooltipHideFooter={tooltipHideFooter}
            legendOnTheRightSide={legendOnTheRightSide}
            hideLegend={hideLegend}
            chartContainerMinHeight={chartContainerMinHeight}
        >
            <canvas
                id={chartId}
                ref={canvasRef}
                tabIndex={0}
                style={{
                    userSelect: "none",
                }}
                aria-label={title}
            >
                {data.datasets.map((set, setKey) =>
                    (set.data as number[]).forEach((item: number, itemKey: number) => (
                        // Generated tooltips for screen readers
                        <div key={itemKey} id={`${chartId}-tooltip-${setKey}-${itemKey}`}>
                            <p>{item}</p>
                            <span>
                                {getText(t.locale, set.label)}: {set.data[itemKey]}
                            </span>
                        </div>
                    ))
                )}
            </canvas>
        </ChartContainer>
    );
};
